import { useCallback, useContext } from 'react';
import { GuardianContext } from '../components/GuardianProvider';
import type { GuardianAgent } from '../components/message-composer/chat-context/selectors';
import { useChat } from './useChat';

/** Depth-first lookup of an agent by id across the tree (top-level + children). */
const findAgentById = (nodes: GuardianAgent[], id: string): GuardianAgent | undefined => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const match = node.children?.length ? findAgentById(node.children, id) : undefined;
    if (match) return match;
  }
  return undefined;
};

/**
 * Reopening an existing conversation loses the in-memory agent (it lives in
 * app-global state, reset on conversation switch). This restores it from the
 * conversation's persisted metadata.
 *
 * Reads `selectedAgent`, what the user picked, never `usedAgents`, what the backend
 * ran: a turn the backend routed or orchestrated chose no agent, and pinning its
 * choice would send it back as `agentSelected` and settle every later turn.
 */
export const useRestoreSelectedAgent = (): ((agents: GuardianAgent[]) => void) => {
  const { selectedAgent, setSelectedAgent, conversationScope } = useContext(GuardianContext);
  const { conversationMetadata } = useChat();

  return useCallback(
    (agents: GuardianAgent[]) => {
      if (conversationScope !== 'chat') return;
      const pinned = conversationMetadata?.selectedAgent;
      if (selectedAgent || !pinned) return;
      const match = findAgentById(agents, pinned);
      if (match) {
        setSelectedAgent(match);
      }
    },
    [selectedAgent, conversationMetadata, conversationScope, setSelectedAgent],
  );
};
